import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import defaultLightLogo from './images/teams/logo-light.png';
import logo from './images/logo-long.svg';
import {
  getApiBaseUrl,
  isSignonUiPreviewEnabled,
  hasSignonApiBases,
  fetchPublicTeamSignon,
  checkParentSignon,
  submitPlayerSignon,
} from './utils/apiBaseUrl';
import './scss/register.scss';
import './scss/matchWelcomePage.scss';

const emptyMedicalItem = () => ({ answer: 'no', details: '' });

function getSignonTotalSteps(skipPasswordStep) {
  return skipPasswordStep ? 4 : 5;
}

function getSignonStepKind(displayStep, skipPasswordStep) {
  const steps = skipPasswordStep
    ? ['parent', 'child', 'medical', 'declarations']
    : ['parent', 'child', 'medical', 'password', 'declarations'];
  return steps[displayStep - 1] || 'parent';
}

function getSignonStepTitle(displayStep, skipPasswordStep) {
  const labels = {
    parent: 'Parent / Guardian',
    child: 'Your child & other information',
    medical: 'Child medical information',
    password: 'FirstWhistle',
    declarations: 'Declarations',
  };
  const kind = getSignonStepKind(displayStep, skipPasswordStep);
  return `Step ${displayStep} — ${labels[kind]}`;
}

const initialMedical = {
  allergies: emptyMedicalItem(),
  prescribed_medicines: emptyMedicalItem(),
  medical_conditions: emptyMedicalItem(),
  learning_ehcp: emptyMedicalItem(),
};

const DECLARATION_KEYS = [
  'perm_training_matches',
  'perm_photos_social',
  'perm_match_reports',
  'medical_treatment',
  'code_of_conduct',
  'respect',
  'social_media_policy',
  'termination',
  'data_protection',
];

function buildDeclarationsPayload(accepted) {
  return DECLARATION_KEYS.reduce((acc, key) => {
    acc[key] = accepted;
    return acc;
  }, {});
}

const SIGNON_TESTIMONIALS = [
  {
    quote:
      'FirstWhistle helps me keep up to date with my child\'s progress — he loves seeing his achievements.',
    name: 'Sarah',
    role: 'Parent',
    avatar: 'S',
  },
  {
    quote: 'My child\'s grandma can now follow his achievements too.',
    name: 'Mark',
    role: 'Parent',
    avatar: 'M',
  },
];

function SignonTestimonialCarousel({ active }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % SIGNON_TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [active]);

  useEffect(() => {
    if (!active) setIndex(0);
  }, [active]);

  const testimonial = SIGNON_TESTIMONIALS[index];

  return (
    <div className="parent-testimonial-section signon-testimonial-carousel">
      <div className="social-proof-card">
        <div className="quote-icon">&quot;</div>
        <p className="testimonial">&quot;{testimonial.quote}&quot;</p>
        <div className="testimonial-author">
          <div className="author-avatar">{testimonial.avatar}</div>
          <div className="author-info">
            <h4>{testimonial.name}, {testimonial.role}</h4>
          </div>
        </div>
      </div>
      <div className="signon-carousel-dots" role="tablist" aria-label="Parent testimonials">
        {SIGNON_TESTIMONIALS.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Testimonial ${i + 1} of ${SIGNON_TESTIMONIALS.length}`}
            className={`signon-carousel-dot${i === index ? ' active' : ''}`}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
    </div>
  );
}

function MedicalQuestion({ id, label, value, onChange, error, onValidate }) {
  const setAnswer = (answer) => {
    onChange({
      answer,
      details: answer === 'no' ? '' : value.details,
    });
  };

  return (
    <div className="form-group signon-medical-question">
      <span className="signon-medical-label">{label}</span>
      <div className="radio-group">
        <label className="radio-label">
          <input
            type="radio"
            name={id}
            checked={value.answer === 'no'}
            onChange={() => setAnswer('no')}
          />
          No
        </label>
        <label className="radio-label">
          <input
            type="radio"
            name={id}
            checked={value.answer === 'yes'}
            onChange={() => setAnswer('yes')}
          />
          Yes
        </label>
      </div>
      {value.answer === 'yes' ? (
        <textarea
          id={`${id}-details`}
          value={value.details}
          onChange={(e) => onChange({ ...value, details: e.target.value })}
          onBlur={onValidate}
          placeholder="Please provide more information"
          className={error ? 'error' : ''}
          aria-label={`${label} — details`}
        />
      ) : null}
      {error ? <span className="error-message">{error}</span> : null}
    </div>
  );
}

function FormField({ id, label, error, children }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? <span className="error-message">{error}</span> : null}
    </div>
  );
}

function SignonPasswordField({ id, label, value, onChange, onBlur, error, autoComplete = 'new-password' }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="form-group signon-password-field">
      <label htmlFor={id}>{label}</label>
      <div className="signon-password-input-wrap">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          className={error ? 'error' : ''}
        />
        <button
          type="button"
          className="signon-password-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
        >
          {visible ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
        </button>
      </div>
      {error ? <span className="error-message">{error}</span> : null}
    </div>
  );
}

function SignonQuestionsSection() {
  return (
    <section className="signon-questions" aria-labelledby="signon-questions-title">
      <h3 id="signon-questions-title" className="signon-questions-title">
        Questions
      </h3>
      <p className="signon-questions-text">
        If you need help completing this form or have any questions about registration, please contact:
      </p>
      <address className="signon-questions-contact">
        <span className="signon-questions-name">Laura Dempster</span>
        <a href="mailto:youtheuafc@outlook.com">youtheuafc@outlook.com</a>
        <a href="tel:+447432084862">07432 084862</a>
      </address>
    </section>
  );
}

function StageIndicator({ currentStep, totalSteps }) {
  return (
    <div className="stage-indicator stage-indicator-compact">
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        return (
          <React.Fragment key={step}>
            {i > 0 ? <div className="stage-line" /> : null}
            <div className={`stage ${currentStep >= step ? 'active' : ''}`}>{step}</div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PlayerSignOnPage() {
  const location = useLocation();
  const teamId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const t = q.get('t');
    return t ? parseInt(t, 10) : 0;
  }, [location.search]);

  const previewMode = isSignonUiPreviewEnabled();

  const [teamInfo, setTeamInfo] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingParent, setCheckingParent] = useState(false);
  const [existingParent, setExistingParent] = useState(false);
  const [parentCheckNotice, setParentCheckNotice] = useState('');
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState({});

  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [parentDob, setParentDob] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [parentPasswordConfirm, setParentPasswordConfirm] = useState('');
  const [relationship, setRelationship] = useState('');

  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [town, setTown] = useState('');
  const [postcode, setPostcode] = useState('');

  const [childFirstNames, setChildFirstNames] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [childDob, setChildDob] = useState('');
  const [childSchool, setChildSchool] = useState('');
  const [lookedAfterChild, setLookedAfterChild] = useState(emptyMedicalItem());

  const [medical, setMedical] = useState(initialMedical);
  const [otherTeams, setOtherTeams] = useState('');
  const [declarationsAccepted, setDeclarationsAccepted] = useState(false);
  const [declarationError, setDeclarationError] = useState('');

  const totalSteps = getSignonTotalSteps(existingParent);
  const stepKind = getSignonStepKind(currentStep, existingParent);

  useEffect(() => {
    setCurrentStep((s) => Math.min(s, getSignonTotalSteps(existingParent)));
  }, [existingParent]);

  useEffect(() => {
    document.body.classList.add('match-welcome-page', 'register-page', 'player-signon-page');
    return () => {
      document.body.classList.remove('match-welcome-page', 'register-page', 'player-signon-page');
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoadError('');
      if (!teamId || teamId < 1) {
        setLoadingTeam(false);
        setLoadError('missing_team_id');
        return;
      }
      if (!getApiBaseUrl() && !previewMode) {
        setLoadingTeam(false);
        setLoadError('API URL is not configured.');
        return;
      }
      try {
        const { team, source } = await fetchPublicTeamSignon(teamId);
        if (team) {
          setTeamInfo(team);
          if (source === 'preview') {
            setLoadError('');
          }
        } else {
          setTeamInfo(null);
          setLoadError('Team not found.');
        }
      } catch (e) {
        console.error(e);
        setTeamInfo(null);
        setLoadError('Could not load team details.');
      } finally {
        setLoadingTeam(false);
      }
    };
    load();
  }, [teamId, previewMode]);

  const validateUkMobile = (v) => /^(\+44|0)7\d{9}$/.test(v.replace(/\s+/g, ''));
  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const validatePostcode = (v) =>
    /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(v.trim().replace(/\s+/g, ' ').trim());

  const getFieldError = (fieldKey, values = {}) => {
    const v = {
      parentFirstName,
      parentLastName,
      parentDob,
      parentEmail,
      parentPhone,
      relationship,
      addressLine1,
      addressLine2,
      town,
      postcode,
      childFirstNames,
      childLastName,
      childDob,
      childSchool,
      lookedAfterChild,
      otherTeams,
      parentPassword,
      parentPasswordConfirm,
      medical,
      ...values,
    };

    switch (fieldKey) {
      case 'parentFirstName':
        return !String(v.parentFirstName).trim() ? 'Required' : undefined;
      case 'parentLastName':
        return !String(v.parentLastName).trim() ? 'Required' : undefined;
      case 'parentDob':
        return !v.parentDob ? 'Required' : undefined;
      case 'parentEmail':
        return !String(v.parentEmail).trim() || !validateEmail(v.parentEmail)
          ? 'Valid email required'
          : undefined;
      case 'parentPhone':
        return !String(v.parentPhone).trim() || !validateUkMobile(v.parentPhone)
          ? 'UK mobile required (e.g. 07…)'
          : undefined;
      case 'relationship':
        return !v.relationship ? 'Required' : undefined;
      case 'addressLine1':
        return !String(v.addressLine1).trim() ? 'Required' : undefined;
      case 'addressLine2':
        return undefined;
      case 'town':
        return !String(v.town).trim() ? 'Required' : undefined;
      case 'postcode':
        return !String(v.postcode).trim() || !validatePostcode(v.postcode)
          ? 'Valid UK postcode required'
          : undefined;
      case 'childFirstNames':
        return !String(v.childFirstNames).trim() ? 'Required' : undefined;
      case 'childLastName':
        return !String(v.childLastName).trim() ? 'Required' : undefined;
      case 'childDob':
        return !v.childDob ? 'Required' : undefined;
      case 'childSchool':
        return !String(v.childSchool).trim() ? 'Required' : undefined;
      case 'lookedAfterChild': {
        const item = v.lookedAfterChild;
        if (!item || (item.answer !== 'yes' && item.answer !== 'no')) {
          return 'Please select Yes or No';
        }
        if (item.answer === 'yes' && !String(item.details).trim()) {
          return 'Please provide more information';
        }
        return undefined;
      }
      case 'parentPassword':
        if (existingParent) return undefined;
        return String(v.parentPassword).length < 6 ? 'At least 6 characters' : undefined;
      case 'parentPasswordConfirm':
        if (existingParent) return undefined;
        return v.parentPassword !== v.parentPasswordConfirm ? 'Passwords do not match' : undefined;
      default:
        break;
    }

    if (fieldKey.startsWith('medical_')) {
      const k = fieldKey.replace('medical_', '');
      const item = v.medical?.[k];
      if (!item) return undefined;
      if (item.answer !== 'yes' && item.answer !== 'no') {
        return 'Please select Yes or No';
      }
      if (item.answer === 'yes' && !String(item.details).trim()) {
        return 'Please provide more information';
      }
    }

    return undefined;
  };

  const getStepErrors = (step) => {
    const e = {};
    const step1Fields = [
      'parentFirstName', 'parentLastName', 'parentDob', 'parentEmail', 'parentPhone',
      'relationship', 'addressLine1', 'town', 'postcode',
    ];
    const step2Fields = ['childFirstNames', 'childLastName', 'childDob', 'childSchool', 'lookedAfterChild'];
    const step4Fields = ['parentPassword', 'parentPasswordConfirm'];

    if (step === 1) {
      step1Fields.forEach((key) => {
        const msg = getFieldError(key);
        if (msg) e[key] = msg;
      });
    }
    if (step === 2) {
      step2Fields.forEach((key) => {
        const msg = getFieldError(key);
        if (msg) e[key] = msg;
      });
    }
    if (step === 3) {
      Object.keys(medical).forEach((k) => {
        const msg = getFieldError(`medical_${k}`);
        if (msg) e[`medical_${k}`] = msg;
      });
    }
    if (getSignonStepKind(step, existingParent) === 'password') {
      step4Fields.forEach((key) => {
        const msg = getFieldError(key);
        if (msg) e[key] = msg;
      });
    }
    return e;
  };

  const updateFieldError = (fieldKey, values = {}) => {
    setErrors((prev) => {
      const message = getFieldError(fieldKey, values);
      if (!message) {
        if (!(fieldKey in prev)) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      }
      if (prev[fieldKey] === message) return prev;
      return { ...prev, [fieldKey]: message };
    });
  };

  const touchField = (fieldKey) => () => updateFieldError(fieldKey);

  const changeField = (setter, fieldKey) => (e) => {
    const value = e.target.value;
    setter(value);
    if (fieldKey === 'parentPhone' || fieldKey === 'parentEmail') {
      setExistingParent(false);
      setParentCheckNotice('');
    }
    const overrides = { [fieldKey]: value };
    const passwordFields = ['parentPassword', 'parentPasswordConfirm'];

    setErrors((prev) => {
      const keysToUpdate = passwordFields.includes(fieldKey)
        ? passwordFields.filter((k) => k in prev)
        : fieldKey in prev
          ? [fieldKey]
          : [];
      if (keysToUpdate.length === 0) return prev;

      let next = { ...prev };
      let changed = false;
      keysToUpdate.forEach((key) => {
        const message = getFieldError(key, overrides);
        if (!message) {
          if (key in next) {
            delete next[key];
            changed = true;
          }
        } else if (prev[key] !== message) {
          next[key] = message;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const touchPasswordFields = () => {
    updateFieldError('parentPassword');
    updateFieldError('parentPasswordConfirm');
  };

  const validateStep = (step) => {
    const e = getStepErrors(step);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateAll = () => {
    const e = {};
    const stepsToValidate = existingParent ? [1, 2, 3] : [1, 2, 3, 4];
    stepsToValidate.forEach((step) => {
      Object.assign(e, getStepErrors(step));
    });
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const firstStepWithError = stepsToValidate.find((step) => Object.keys(getStepErrors(step)).length > 0);
      if (firstStepWithError) setCurrentStep(firstStepWithError);
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    setApiError('');
    if (!validateStep(currentStep)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    let skipPasswordStep = existingParent;

    if (currentStep === 1) {
      if (!hasSignonApiBases()) {
        setApiError(
          previewMode
            ? 'Sign-on API is not configured. Set REACT_APP_API_URL or REACT_APP_LOCAL_API_URL.'
            : 'Could not reach the sign-on API. Please try again later.'
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      setCheckingParent(true);
      try {
        const result = await checkParentSignon({
          parent_phone: parentPhone.trim(),
        });
        if (!result.success) {
          setApiError(result.message || 'Could not verify your details. Please try again.');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        skipPasswordStep = Boolean(result.data?.skip_password);
        setExistingParent(skipPasswordStep);
        setParentCheckNotice(skipPasswordStep ? (result.data?.message || '') : '');
      } catch (err) {
        console.error(err);
        setApiError('Could not verify your details. Please check your connection and try again.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      } finally {
        setCheckingParent(false);
      }
    }

    const maxStep = getSignonTotalSteps(skipPasswordStep);
    setErrors({});
    setCurrentStep((s) => Math.min(s + 1, maxStep));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setErrors({});
    setApiError('');
    setDeclarationError('');
    setCurrentStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setApiError('');
    setDeclarationError('');

    if (!validateAll()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!declarationsAccepted) {
      setDeclarationError('You must accept all declarations to continue.');
      setCurrentStep(totalSteps);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        team_id: teamId,
        parent_first_name: parentFirstName.trim(),
        parent_last_name: parentLastName.trim(),
        parent_dob: parentDob,
        parent_email: parentEmail.trim(),
        parent_phone: parentPhone.trim(),
        relationship,
        address_line1: addressLine1.trim(),
        address_line2: addressLine2.trim(),
        town: town.trim(),
        postcode: postcode.trim(),
        child_first_names: childFirstNames.trim(),
        child_last_name: childLastName.trim(),
        child_dob: childDob,
        child_school_sept_2026: childSchool.trim(),
        looked_after_child: lookedAfterChild,
        medical,
        other_teams_leagues: otherTeams.trim(),
        declarations: buildDeclarationsPayload(declarationsAccepted),
      };
      if (!existingParent) {
        body.parent_password = parentPassword;
      }
      const result = await submitPlayerSignon(body);
      if (result.success) {
        setSubmitSuccessMessage(
          result.data?.message
            || 'Thank you. Your sign-on has been submitted and is pending approval from the club.'
        );
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setApiError(result.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const teamLogo = teamInfo
    ? `https://football.pebbledesigns.co.uk/api/images/teams/${teamInfo.team_id}/logo-dark.png`
    : null;

  if (loadingTeam) {
    return (
      <div className="match-welcome-page player-signon-page">
        <div className="welcome-container welcome-container--signon">
          <div className="loading-container">
            <div className="loading">Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  if (!teamId || !teamInfo) {
    return (
      <div className="match-welcome-page player-signon-page">
        <div className="welcome-container welcome-container--signon">
          <div className="register-container">
            <div className="thank-you-message">
              <h3>{loadError === 'missing_team_id' ? 'Invalid link' : 'Team not found'}</h3>
              <p>
                {loadError === 'missing_team_id'
                  ? 'Use a valid link including your team reference, e.g. /player-sign-on?t=101'
                  : loadError || `No team with ID ${teamId} was returned from the API.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="match-welcome-page player-signon-page">
        <Helmet>
          <title>Sign-on submitted – {teamInfo.team_name}</title>
        </Helmet>
        <div className="welcome-container welcome-container--signon">
          <div className="register-container">
            <div className="thank-you-screen">
              <h1>Thank you</h1>
              <p className="thank-you-message">
                {submitSuccessMessage || (
                  <>
                    Your player sign-on for <strong>{teamInfo.team_name}</strong> has been received. The club will
                    review your details and your FirstWhistle parent account will be fully active once approved.
                  </>
                )}
              </p>
            </div>
          </div>
          <SignonQuestionsSection />
          <footer className="welcome-footer">
            <div className="firstwhistle-logo-container">
              <img src={logo} alt="FirstWhistle Logo" className="firstwhistle-logo" />
            </div>
            <p>Powered by FirstWhistle</p>
          </footer>
        </div>
      </div>
    );
  }

  const declarationItems = [
    ['perm_training_matches', 'I/we give permission for my child (named above) to attend training and to represent Eastbourne United AFC during 2026/27 season (in friendly matches, competitive matches and tournaments).'],
    ['perm_photos_social', 'I/we give permission for the club to take photographs and use on the club website and social media.'],
    ['perm_match_reports', 'I/we give permission to the club to publish match reports in which players are identified by first name (we cannot publish scores for younger age groups).'],
    ['medical_treatment', 'Medical treatment — I/we give permission for club officials to provide first aid to the player in the event of illness or injury. I/we understand that whilst the club’s coaches, officials and helpers will take all reasonable care and precautions to ensure the health and safety of players, they cannot be held responsible for any loss, damage or injury suffered by the player whilst travelling to and from fixtures or whilst participating in matches or training sessions.'],
    ['code_of_conduct', 'Code of conduct — I/we have read and the player (named above) has read and agrees to abide by the rules, regulations and Codes of Conduct of Eastbourne United AFC (these can be found on our website or as a paper copy in the clubhouse).'],
    ['respect', 'Respect — I/we have read and the player (named above) has read and understands the FA’s Respect documents.'],
    ['social_media_policy', 'Social media — I/we have read and the player (named above) has read and understands the club’s social media policy. The club’s Code of Conduct, Respect, social media and privacy policy documents can be emailed to you separately or printed if requested.'],
    ['termination', 'Termination — I/we understand the club reserves the right to cancel membership without refund in the event that conduct, on or off the pitch, is deemed by the club to be unacceptable.'],
    ['data_protection', 'Data protection — I certify that the above information is correct and I consent to the information I have provided on this form being used by the club for any purposes under the GDPR Act 2018. I also certify that I do not have outstanding liabilities with any clubs I have been previously registered with. I understand that failure to disclose such information or making any false statement will render the registration invalid and liable for sanction.'],
  ];

  const medicalFields = [
    ['allergies', 'Does the player have any allergies?'],
    ['prescribed_medicines', 'Is the player regularly taking prescribed medicines?'],
    ['medical_conditions', 'Does the player have any medical conditions?'],
    ['learning_ehcp', 'Does the player have any learning difficulties? Does your child have an Education, Health and Care Plan (EHCP)?'],
  ];

  return (
    <div className="match-welcome-page player-signon-page">
      <Helmet>
        <title>Player Sign On – {teamInfo.team_name}</title>
        <meta name="description" content={`Player sign-on for ${teamInfo.team_name} via FirstWhistle.`} />
      </Helmet>
      <div className="welcome-container welcome-container--signon">
        <div className="match-confirmation-section">
          <div className="teams-display-vertical">
            <div className="team-card-vertical current-team">
              <div className="shield-container">
                <img
                  className="team-shield"
                  src={teamLogo}
                  alt={teamInfo.team_name}
                  onError={(e) => {
                    e.target.src = defaultLightLogo;
                  }}
                />
              </div>
              <div className="team-name">{teamInfo.team_name}</div>
            </div>
          </div>
          <h1 className="match-confirmation-title">Player Sign On</h1>
          <div className="match-welcome-message">
            <p>
              {teamInfo.team_name} — complete this form to register your child for the 2026/27 season.
            </p>
          </div>
        </div>

        <div className="register-container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="register-stage">
              <StageIndicator currentStep={currentStep} totalSteps={totalSteps} />
              <h3 className="stage-title">{getSignonStepTitle(currentStep, existingParent)}</h3>

              {stepKind === 'parent' && (
                <>
                  <FormField id="pf" label="First name" error={errors.parentFirstName}>
                    <input id="pf" type="text" value={parentFirstName} onChange={changeField(setParentFirstName, 'parentFirstName')} onBlur={touchField('parentFirstName')} className={errors.parentFirstName ? 'error' : ''} />
                  </FormField>
                  <FormField id="pl" label="Surname" error={errors.parentLastName}>
                    <input id="pl" type="text" value={parentLastName} onChange={changeField(setParentLastName, 'parentLastName')} onBlur={touchField('parentLastName')} className={errors.parentLastName ? 'error' : ''} />
                  </FormField>
                  <FormField id="pdob" label="Date of birth" error={errors.parentDob}>
                    <input id="pdob" type="date" value={parentDob} onChange={changeField(setParentDob, 'parentDob')} onBlur={touchField('parentDob')} className={errors.parentDob ? 'error' : ''} />
                  </FormField>
                  <FormField id="pe" label="Email" error={errors.parentEmail}>
                    <input id="pe" type="email" autoComplete="email" value={parentEmail} onChange={changeField(setParentEmail, 'parentEmail')} onBlur={touchField('parentEmail')} className={errors.parentEmail ? 'error' : ''} />
                  </FormField>
                  <FormField id="a1" label="Address line 1" error={errors.addressLine1}>
                    <input id="a1" type="text" value={addressLine1} onChange={changeField(setAddressLine1, 'addressLine1')} onBlur={touchField('addressLine1')} className={errors.addressLine1 ? 'error' : ''} />
                  </FormField>
                  <FormField id="a2" label="Address line 2 (optional)" error={errors.addressLine2}>
                    <input id="a2" type="text" value={addressLine2} onChange={changeField(setAddressLine2, 'addressLine2')} onBlur={touchField('addressLine2')} className={errors.addressLine2 ? 'error' : ''} />
                  </FormField>
                  <FormField id="town" label="Town / city" error={errors.town}>
                    <input id="town" type="text" value={town} onChange={changeField(setTown, 'town')} onBlur={touchField('town')} className={errors.town ? 'error' : ''} />
                  </FormField>
                  <FormField id="pc" label="Post code" error={errors.postcode}>
                    <input id="pc" type="text" value={postcode} onChange={changeField(setPostcode, 'postcode')} onBlur={touchField('postcode')} className={errors.postcode ? 'error' : ''} />
                  </FormField>
                  <FormField id="pm" label="Mobile number" error={errors.parentPhone}>
                    <input id="pm" type="tel" autoComplete="tel" placeholder="07xxx xxxxxx" value={parentPhone} onChange={changeField(setParentPhone, 'parentPhone')} onBlur={touchField('parentPhone')} className={errors.parentPhone ? 'error' : ''} />
                  </FormField>
                  <FormField id="rel" label="Relationship to child" error={errors.relationship}>
                    <select id="rel" value={relationship} onChange={changeField(setRelationship, 'relationship')} onBlur={touchField('relationship')} className={errors.relationship ? 'error' : ''}>
                      <option value="">Select relationship</option>
                      <option value="Mother">Mother</option>
                      <option value="Father">Father</option>
                      <option value="Step-mother">Step-mother</option>
                      <option value="Step-father">Step-father</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Grandmother">Grandmother</option>
                      <option value="Grandfather">Grandfather</option>
                      <option value="Aunt">Aunt</option>
                      <option value="Uncle">Uncle</option>
                      <option value="Other">Other</option>
                    </select>
                  </FormField>
                  {apiError && stepKind === 'parent' ? (
                    <span className="error-message">{apiError}</span>
                  ) : null}
                  {parentCheckNotice ? (
                    <p className="signon-existing-parent-notice">{parentCheckNotice}</p>
                  ) : null}
                </>
              )}

              {stepKind === 'child' && (
                <>
                  {existingParent ? (
                    <p className="signon-existing-parent-notice">
                      Using your existing FirstWhistle parent account — no new password needed.
                    </p>
                  ) : null}
                  <FormField id="cf" label="Forename(s)" error={errors.childFirstNames}>
                    <input id="cf" type="text" value={childFirstNames} onChange={changeField(setChildFirstNames, 'childFirstNames')} onBlur={touchField('childFirstNames')} className={errors.childFirstNames ? 'error' : ''} />
                  </FormField>
                  <FormField id="cl" label="Surname" error={errors.childLastName}>
                    <input id="cl" type="text" value={childLastName} onChange={changeField(setChildLastName, 'childLastName')} onBlur={touchField('childLastName')} className={errors.childLastName ? 'error' : ''} />
                  </FormField>
                  <FormField id="cdob" label="Date of birth" error={errors.childDob}>
                    <input id="cdob" type="date" value={childDob} onChange={changeField(setChildDob, 'childDob')} onBlur={touchField('childDob')} className={errors.childDob ? 'error' : ''} />
                  </FormField>
                  <FormField id="sch" label="School (Sept 2026)" error={errors.childSchool}>
                    <input id="sch" type="text" value={childSchool} onChange={changeField(setChildSchool, 'childSchool')} onBlur={touchField('childSchool')} className={errors.childSchool ? 'error' : ''} />
                  </FormField>
                  <MedicalQuestion
                    id="looked-after"
                    label="Is the child a ‘looked after child’ or a previously ‘looked after child’?"
                    value={lookedAfterChild}
                    onChange={(next) => {
                      setLookedAfterChild(next);
                      updateFieldError('lookedAfterChild', { lookedAfterChild: next });
                    }}
                    onValidate={() => updateFieldError('lookedAfterChild')}
                    error={errors.lookedAfterChild}
                  />
                  <FormField id="ot" label="Name any other teams and leagues that the player is signed/signing for, or team signed for last season (optional)" error={errors.otherTeams}>
                    <textarea id="ot" value={otherTeams} onChange={changeField(setOtherTeams, 'otherTeams')} onBlur={touchField('otherTeams')} className={errors.otherTeams ? 'error' : ''} />
                  </FormField>
                </>
              )}

              {stepKind === 'medical' && (
                <>
                  {medicalFields.map(([key, label]) => (
                    <MedicalQuestion
                      key={key}
                      id={key}
                      label={label}
                      value={medical[key]}
                      onChange={(next) => {
                        const nextMedical = { ...medical, [key]: next };
                        setMedical(nextMedical);
                        updateFieldError(`medical_${key}`, { medical: nextMedical });
                      }}
                      onValidate={() => updateFieldError(`medical_${key}`)}
                      error={errors[`medical_${key}`]}
                    />
                  ))}
                </>
              )}

              {stepKind === 'password' && (
                <>
                  <div className="signon-fw-intro">
                    <div className="firstwhistle-logo-container">
                      <img src={logo} alt="FirstWhistle Logo" className="firstwhistle-logo" />
                    </div>
                    <p>
                      FirstWhistle keeps parents connected with match updates, reports and achievements.
                      Create a password for your parent account — you&apos;ll use it to log in once the club approves your sign-on.
                    </p>
                  </div>
                  <SignonPasswordField
                    id="pp"
                    label="Password"
                    value={parentPassword}
                    onChange={changeField(setParentPassword, 'parentPassword')}
                    onBlur={touchPasswordFields}
                    error={errors.parentPassword}
                  />
                  <SignonPasswordField
                    id="ppc"
                    label="Confirm password"
                    value={parentPasswordConfirm}
                    onChange={changeField(setParentPasswordConfirm, 'parentPasswordConfirm')}
                    onBlur={touchPasswordFields}
                    error={errors.parentPasswordConfirm}
                  />
                  <SignonTestimonialCarousel active={stepKind === 'password'} />
                </>
              )}

              {stepKind === 'declarations' && (
                <>
                  <ul className="signon-declaration-text">
                    {declarationItems.map(([key, text]) => (
                      <li key={key}>{text}</li>
                    ))}
                  </ul>
                  <div className="form-group signon-declaration-accept">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={declarationsAccepted}
                        onChange={(e) => {
                          setDeclarationsAccepted(e.target.checked);
                          if (e.target.checked) setDeclarationError('');
                        }}
                      />
                      <span>I accept all of the above declarations</span>
                    </label>
                  </div>
                  {declarationError ? (
                    <span className="error-message signon-declaration-error">{declarationError}</span>
                  ) : null}
                  {apiError ? <span className="error-message">{apiError}</span> : null}
                </>
              )}

              <div className="signon-nav">
                {currentStep > 1 ? (
                  <button type="button" className="signon-nav-btn signon-nav-btn--back" onClick={handleBack}>
                    Back
                  </button>
                ) : null}
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    className="signon-nav-btn signon-nav-btn--next"
                    onClick={handleNext}
                    disabled={checkingParent}
                  >
                    {checkingParent ? 'Checking…' : 'Next'}
                  </button>
                ) : (
                  <button type="submit" className="signon-nav-btn signon-nav-btn--next" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit sign-on'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        <SignonQuestionsSection />

        <footer className="welcome-footer">
          <div className="firstwhistle-logo-container">
            <img src={logo} alt="FirstWhistle Logo" className="firstwhistle-logo" />
          </div>
          <p>Powered by FirstWhistle</p>
        </footer>
      </div>
    </div>
  );
}

export default PlayerSignOnPage;
